// src/components/OrdersView.js
import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Receipt,
  Calendar,
  Trash2,
  Search,
  X,
  Eye,
} from "lucide-react";

import { getOrders, deleteOrder } from "../api";
import { getRoleFromToken } from "../auth";

export default function OrdersView() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchOrderNumber, setSearchOrderNumber] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [selectedOrder, setSelectedOrder] = useState(null);

  const role = getRoleFromToken();
  const firstLoadRef = useRef(true);

  // ---------------------------------------------------------
  // LOAD ORDERS
  // ---------------------------------------------------------

  const loadOrders = useCallback(async (silent = false) => {
    try {
      if (!silent && firstLoadRef.current) {
        setLoading(true);
      }

      const data = await getOrders();

      const orderList = Array.isArray(data) ? data : [];

      setOrders((prev) => {
        return JSON.stringify(prev) !== JSON.stringify(orderList)
          ? orderList
          : prev;
      });

      firstLoadRef.current = false;
    } catch (e) {
      console.error("Error loading orders:", e);
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, []);

  // ---------------------------------------------------------
  // INITIAL LOAD + AUTO REFRESH
  // ---------------------------------------------------------

  useEffect(() => {
    loadOrders(false);

    const intervalId = setInterval(() => {
      loadOrders(true);
    }, 5000);

    return () => clearInterval(intervalId);
  }, [loadOrders]);

  // ---------------------------------------------------------
  // FORMAT DATE
  // ---------------------------------------------------------

  const formatDate = (dateString) => {
    if (!dateString) return "";

    return new Date(dateString).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // ---------------------------------------------------------
  // FORMAT ORDER TYPE
  // ---------------------------------------------------------

  const getOrderType = (order) => {
    if (order?.orderType === "TAKEAWAY") {
      return {
        label: "Takeaway",
        icon: "🥡",
        className: "bg-green-100 text-green-700",
      };
    }

    return {
      label: "Dine In",
      icon: "🍽️",
      className: "bg-blue-100 text-blue-700",
    };
  };

  // ---------------------------------------------------------
  // STATUS
  // ---------------------------------------------------------

  const getStatusClass = (status) => {
    switch (status?.toUpperCase()) {
      case "READY":
        return "bg-green-100 text-green-700";

      case "PENDING":
        return "bg-yellow-100 text-yellow-700";

      case "COMPLETED":
        return "bg-green-100 text-green-700";

      case "DRAFT":
        return "bg-slate-100 text-slate-700";

      default:
        return "bg-slate-100 text-slate-700";
    }
  };

  // ---------------------------------------------------------
  // TOTAL ITEMS
  // ---------------------------------------------------------

  const getTotalItems = (order) => {
    return (order?.orderItems || []).reduce(
      (sum, item) => sum + Number(item.quantity || 0),
      0
    );
  };

  // ---------------------------------------------------------
  // DISCOUNT
  // ---------------------------------------------------------

  const getDiscountPercent = (order) => {
    return Number(order?.discountPercent || 0);
  };

  const getDiscountAmount = (order) => {
    return Number(order?.discountAmount || 0);
  };

  // ---------------------------------------------------------
  // SUBTOTAL
  // ---------------------------------------------------------

  const getSubtotal = (order) => {
    if (order?.subtotal !== undefined && order?.subtotal !== null) {
      return Number(order.subtotal || 0);
    }

    return (order?.orderItems || []).reduce(
      (sum, item) =>
        sum +
        Number(item.price || 0) *
          Number(item.quantity || 0),
      0
    );
  };

  // ---------------------------------------------------------
  // TAX
  // ---------------------------------------------------------

  const getTaxAmount = (order) => {
    if (order?.taxAmount !== undefined && order?.taxAmount !== null) {
      return Number(order.taxAmount || 0);
    }

    return (order?.orderItems || []).reduce(
      (sum, item) => sum + Number(item.taxAmount || 0),
      0
    );
  };

  // ---------------------------------------------------------
  // TOTAL
  // ---------------------------------------------------------

  const getTotal = (order) => {
    if (
      order?.totalAmount !== undefined &&
      order?.totalAmount !== null
    ) {
      return Number(order.totalAmount || 0);
    }

    return (
      getSubtotal(order) -
      getDiscountAmount(order) +
      getTaxAmount(order)
    );
  };

  // ---------------------------------------------------------
  // DELETE
  // ---------------------------------------------------------

  const handleDelete = async (id) => {
    if (role !== "ADMIN") {
      alert("Only admin can delete orders.");
      return;
    }

    if (!window.confirm("Delete this order?")) {
      return;
    }

    try {
      await deleteOrder(id);

      if (selectedOrder?.id === id) {
        setSelectedOrder(null);
      }

      await loadOrders(true);
    } catch (err) {
      console.error("Delete failed:", err);
      alert("Failed to delete order");
    }
  };

  // ---------------------------------------------------------
  // CLEAR FILTERS
  // ---------------------------------------------------------

  const clearFilters = () => {
    setSearchOrderNumber("");
    setFromDate("");
    setToDate("");
  };

  // ---------------------------------------------------------
  // FILTER
  // ---------------------------------------------------------

  const filteredOrders = orders.filter((order) => {
    const search = searchOrderNumber.trim().toLowerCase();

    const matchesOrderNumber =
      search === "" ||
      String(order.orderNumber || "")
        .toLowerCase()
        .includes(search);

    if (!matchesOrderNumber) {
      return false;
    }

    if (fromDate || toDate) {
      if (!order.createdAt) {
        return false;
      }

      const orderDate = new Date(order.createdAt);

      const orderDateString = `${orderDate.getFullYear()}-${String(
        orderDate.getMonth() + 1
      ).padStart(2, "0")}-${String(
        orderDate.getDate()
      ).padStart(2, "0")}`;

      if (fromDate && orderDateString < fromDate) {
        return false;
      }

      if (toDate && orderDateString > toDate) {
        return false;
      }
    }

    return true;
  });

  // ---------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------

  return (
    <div className="p-4 sm:p-8 h-full overflow-auto">

      {/* HEADER */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">

        <div>
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-800">
            Order History
          </h2>

          <p className="text-sm text-slate-500 mt-1">
            Search and manage your restaurant orders
          </p>
        </div>

        <div className="bg-white shadow-sm rounded-lg px-4 py-2">
          <div className="text-xs text-slate-500">
            Showing Orders
          </div>

          <div className="text-xl font-bold text-slate-800">
            {filteredOrders.length}
          </div>
        </div>
      </div>

      {/* SEARCH + FILTER */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-6">

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">

          {/* SEARCH */}
          <div className="xl:col-span-2">

            <label className="block text-sm font-medium text-slate-600 mb-1">
              Search Order Number
            </label>

            <div className="relative">

              <Search
                size={18}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />

              <input
                type="text"
                value={searchOrderNumber}
                onChange={(e) =>
                  setSearchOrderNumber(e.target.value)
                }
                placeholder="Search by order number..."
                className="w-full border border-slate-300 rounded-lg pl-10 pr-4 py-2.5 outline-none focus:ring-2 focus:ring-blue-500"
              />

            </div>
          </div>

          {/* FROM */}
          <div>

            <label className="block text-sm font-medium text-slate-600 mb-1">
              From Date
            </label>

            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-3 py-2.5 outline-none focus:ring-2 focus:ring-blue-500"
            />

          </div>

          {/* TO */}
          <div>

            <label className="block text-sm font-medium text-slate-600 mb-1">
              To Date
            </label>

            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-3 py-2.5 outline-none focus:ring-2 focus:ring-blue-500"
            />

          </div>

        </div>

        {(searchOrderNumber || fromDate || toDate) && (
          <div className="mt-4 flex justify-end">

            <button
              onClick={clearFilters}
              className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-sm font-medium"
            >
              <X size={16} />
              Clear Filters
            </button>

          </div>
        )}

      </div>

      {/* LOADING */}
      {loading ? (

        <div className="text-center py-12">
          <p className="text-slate-500">
            Loading orders…
          </p>
        </div>

      ) : filteredOrders.length === 0 ? (

        <div className="text-center py-12 bg-white rounded-xl shadow-sm">

          <Receipt
            size={48}
            className="mx-auto mb-4 text-slate-300"
          />

          {orders.length === 0 ? (

            <p className="text-slate-500">
              No orders yet
            </p>

          ) : (

            <>
              <p className="text-slate-600 font-medium">
                No orders found
              </p>

              <p className="text-sm text-slate-400 mt-1">
                Try changing your search or date filters.
              </p>
            </>

          )}

        </div>

      ) : (

        /* ORDER LIST */
        <div className="space-y-4">

          {filteredOrders.map((order) => {

            const orderType = getOrderType(order);
            const subtotal = getSubtotal(order);
            const taxAmount = getTaxAmount(order);
            const discountAmount = getDiscountAmount(order);
            const discountPercent = getDiscountPercent(order);
            const total = getTotal(order);

            return (

              <div
                key={order.id}
                className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 sm:p-6"
              >

                {/* HEADER */}
                <div className="flex flex-col sm:flex-row sm:justify-between gap-4 mb-4">

                  <div>

                    <div className="flex flex-wrap items-center gap-2">

                      <h3 className="text-lg font-bold text-slate-800">
                        {order.orderNumber}
                      </h3>

                      <span
                        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${orderType.className}`}
                      >
                        {orderType.icon}
                        {orderType.label}
                      </span>

                      {order.orderType === "DINE_IN" &&
                        order.table && (
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-purple-100 text-purple-700 text-xs font-semibold">
                            Table: {order.table.tableName}
                          </span>
                        )}

                    </div>

                    <div className="flex items-center gap-2 text-xs text-slate-500 mt-2">

                      <Calendar size={14} />

                      {formatDate(order.createdAt)}

                    </div>

                  </div>

                  {/* TOTAL */}
                  <div className="sm:text-right">

                    <div className="text-xl font-bold text-green-600">
                      ₹{total.toFixed(2)}
                    </div>

                    <span
                      className={`inline-block mt-1 px-3 py-1 rounded-full text-xs font-medium ${getStatusClass(
                        order.status
                      )}`}
                    >
                      {order.status}
                    </span>

                  </div>

                </div>

                {/* ORDER NOTE */}
                {order.specialInstructions &&
                  order.specialInstructions.trim() !== "" && (

                    <div className="mb-4 rounded-lg border border-yellow-200 bg-yellow-50 p-3">

                      <div className="text-xs font-semibold text-yellow-800 mb-1">
                        📝 Order Note
                      </div>

                      <div className="text-sm text-slate-700">
                        {order.specialInstructions}
                      </div>

                    </div>
                  )}

                {/* ITEMS */}
                <div className="border-t pt-4">

                  <div className="flex justify-between items-center mb-2">

                    <h4 className="font-semibold text-slate-700">
                      Items
                    </h4>

                    <span className="text-xs text-slate-500">
                      {getTotalItems(order)} item
                      {getTotalItems(order) !== 1 ? "s" : ""}
                    </span>

                  </div>

                  <div className="space-y-2 text-sm">

                    {(order.orderItems || []).map((item) => {

                      const itemPrice = Number(item.price || 0);
                      const quantity = Number(item.quantity || 0);
                      const itemTax = Number(item.taxAmount || 0);
                      const itemLineTotal =
                        Number(item.lineTotal || 0);

                      return (

                        <div
                          key={item.id}
                          className="border-b last:border-b-0 pb-2"
                        >

                          <div className="flex justify-between gap-4">

                            <span className="text-slate-700">
                              {quantity} ×{" "}
                              {item.menuItem?.name || "Unknown Item"}
                            </span>

                            <span className="font-medium text-slate-700 whitespace-nowrap">
                              ₹
                              {(
                                itemLineTotal ||
                                itemPrice * quantity
                              ).toFixed(2)}
                            </span>

                          </div>

                          {/* ITEM TAX */}
                          {itemTax > 0 && (
                            <div className="flex justify-between text-xs text-slate-500 mt-1">

                              <span>
                                GST{" "}
                                {Number(
                                  item.taxRate || 0
                                ).toFixed(2)}
                                %
                              </span>

                              <span>
                                + ₹{itemTax.toFixed(2)}
                              </span>

                            </div>
                          )}

                          {item.specialInstructions &&
                            item.specialInstructions.trim() !== "" && (
                              <div className="text-xs text-yellow-700 mt-1">
                                📝 {item.specialInstructions}
                              </div>
                            )}

                        </div>

                      );
                    })}

                  </div>

                </div>

                {/* BILL SUMMARY */}
                <div className="mt-5 pt-4 border-t">

                  <div className="space-y-2 text-sm">

                    <div className="flex justify-between">
                      <span className="text-slate-500">
                        Subtotal
                      </span>

                      <span className="font-medium">
                        ₹{subtotal.toFixed(2)}
                      </span>
                    </div>

                    {discountAmount > 0 && (
                      <div className="flex justify-between text-red-600">

                        <span>
                          Discount
                          {discountPercent > 0
                            ? ` (${discountPercent.toFixed(2)}%)`
                            : ""}
                        </span>

                        <span>
                          - ₹{discountAmount.toFixed(2)}
                        </span>

                      </div>
                    )}

                    <div className="flex justify-between text-slate-600">

                      <span>
                        GST / Tax
                      </span>

                      <span>
                        ₹{taxAmount.toFixed(2)}
                      </span>

                    </div>

                    <div className="flex justify-between items-center pt-3 border-t">

                      <span className="text-lg font-semibold text-slate-700">
                        Total
                      </span>

                      <span className="text-2xl font-bold text-green-600">
                        ₹{total.toFixed(2)}
                      </span>

                    </div>

                  </div>

                </div>

                {/* ACTIONS */}
                <div className="mt-4 pt-4 border-t flex flex-col sm:flex-row sm:justify-end gap-2">

                  <button
                    onClick={() => setSelectedOrder(order)}
                    className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-200"
                  >
                    <Eye size={16} />
                    View Details
                  </button>

                  {role === "ADMIN" && (
                    <button
                      onClick={() => handleDelete(order.id)}
                      className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-red-100 text-red-700 rounded-lg text-sm font-medium hover:bg-red-200"
                    >
                      <Trash2 size={14} />
                      Delete
                    </button>
                  )}

                </div>

              </div>

            );
          })}

        </div>
      )}

      {/* =====================================================
          ORDER DETAILS MODAL
      ====================================================== */}

      {selectedOrder && (

        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={() => setSelectedOrder(null)}
        >

          <div
            className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >

            {/* MODAL HEADER */}
            <div className="sticky top-0 bg-white border-b p-5 flex justify-between items-start">

              <div>

                <h2 className="text-xl font-bold text-slate-800">
                  Order Details
                </h2>

                <p className="text-sm text-slate-500 mt-1">
                  {selectedOrder.orderNumber}
                </p>

              </div>

              <button
                onClick={() => setSelectedOrder(null)}
                className="p-2 rounded-lg hover:bg-slate-100"
              >
                <X size={20} />
              </button>

            </div>

            {/* MODAL CONTENT */}
            <div className="p-5 space-y-5">

              {/* BASIC INFORMATION */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">

                <div className="bg-slate-50 rounded-lg p-3">
                  <div className="text-xs text-slate-500">
                    Order Number
                  </div>

                  <div className="font-semibold text-slate-800 mt-1">
                    {selectedOrder.orderNumber}
                  </div>
                </div>

                <div className="bg-slate-50 rounded-lg p-3">
                  <div className="text-xs text-slate-500">
                    Date & Time
                  </div>

                  <div className="font-semibold text-slate-800 mt-1">
                    {formatDate(selectedOrder.createdAt)}
                  </div>
                </div>

                <div className="bg-slate-50 rounded-lg p-3">

                  <div className="text-xs text-slate-500">
                    Order Type
                  </div>

                  <div className="font-semibold text-slate-800 mt-1">
                    {getOrderType(selectedOrder).icon}{" "}
                    {getOrderType(selectedOrder).label}
                  </div>

                </div>

                <div className="bg-slate-50 rounded-lg p-3">

                  <div className="text-xs text-slate-500">
                    Table
                  </div>

                  <div className="font-semibold text-slate-800 mt-1">

                    {selectedOrder.orderType === "DINE_IN" &&
                    selectedOrder.table
                      ? selectedOrder.table.tableName
                      : "—"}

                  </div>

                </div>

                <div className="bg-slate-50 rounded-lg p-3">

                  <div className="text-xs text-slate-500">
                    Status
                  </div>

                  <div className="mt-1">

                    <span
                      className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${getStatusClass(
                        selectedOrder.status
                      )}`}
                    >
                      {selectedOrder.status}
                    </span>

                  </div>

                </div>

                <div className="bg-slate-50 rounded-lg p-3">

                  <div className="text-xs text-slate-500">
                    Total Amount
                  </div>

                  <div className="font-bold text-green-600 text-lg mt-1">
                    ₹{getTotal(selectedOrder).toFixed(2)}
                  </div>

                </div>

              </div>

              {/* DISCOUNT */}
              <div>

                <h3 className="font-semibold text-slate-800 mb-2">
                  Billing Summary
                </h3>

                <div className="rounded-lg border bg-slate-50 p-4 space-y-2">

                  <div className="flex justify-between text-sm">
                    <span>Subtotal</span>

                    <span>
                      ₹{getSubtotal(selectedOrder).toFixed(2)}
                    </span>
                  </div>

                  {getDiscountAmount(selectedOrder) > 0 && (
                    <div className="flex justify-between text-sm text-red-600">

                      <span>
                        Discount (
                        {getDiscountPercent(
                          selectedOrder
                        ).toFixed(2)}
                        %)
                      </span>

                      <span>
                        - ₹
                        {getDiscountAmount(
                          selectedOrder
                        ).toFixed(2)}
                      </span>

                    </div>
                  )}

                  <div className="flex justify-between text-sm">

                    <span>
                      GST / Tax
                    </span>

                    <span>
                      ₹
                      {getTaxAmount(
                        selectedOrder
                      ).toFixed(2)}
                    </span>

                  </div>

                  <div className="border-t pt-3 flex justify-between">

                    <span className="font-semibold">
                      Total
                    </span>

                    <span className="font-bold text-green-600">
                      ₹
                      {getTotal(
                        selectedOrder
                      ).toFixed(2)}
                    </span>

                  </div>

                </div>

              </div>

              {/* ORDER NOTE */}
              <div>

                <h3 className="font-semibold text-slate-800 mb-2">
                  📝 Order Note
                </h3>

                {selectedOrder.specialInstructions &&
                selectedOrder.specialInstructions.trim() !== "" ? (

                  <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4 text-sm text-slate-700">
                    {selectedOrder.specialInstructions}
                  </div>

                ) : (

                  <div className="rounded-lg bg-slate-50 p-4 text-sm text-slate-400">
                    No notes added for this order.
                  </div>

                )}

              </div>

              {/* ITEMS */}
              <div>

                <div className="flex justify-between items-center mb-3">

                  <h3 className="font-semibold text-slate-800">
                    Items
                  </h3>

                  <span className="text-sm text-slate-500">
                    {getTotalItems(selectedOrder)} total
                  </span>

                </div>

                <div className="border rounded-lg overflow-hidden">

                  {(selectedOrder.orderItems || []).map(
                    (item, index) => {

                      const price = Number(
                        item.price || 0
                      );

                      const quantity = Number(
                        item.quantity || 0
                      );

                      const lineTotal =
                        Number(item.lineTotal || 0) ||
                        price * quantity;

                      const taxRate = Number(
                        item.taxRate || 0
                      );

                      const taxAmount = Number(
                        item.taxAmount || 0
                      );

                      return (

                        <div
                          key={item.id}
                          className={`p-3 ${
                            index !==
                            selectedOrder.orderItems.length - 1
                              ? "border-b"
                              : ""
                          }`}
                        >

                          <div className="flex justify-between gap-4">

                            <div>

                              <div className="font-medium text-slate-800">
                                {item.menuItem?.name ||
                                  "Unknown Item"}
                              </div>

                              <div className="text-xs text-slate-500 mt-1">
                                ₹{price.toFixed(2)} ×{" "}
                                {quantity}
                              </div>

                              {taxRate > 0 && (
                                <div className="text-xs text-slate-500 mt-1">
                                  GST {taxRate.toFixed(2)}%
                                  {" • "}
                                  ₹{taxAmount.toFixed(2)}
                                </div>
                              )}

                              {item.specialInstructions &&
                                item.specialInstructions.trim() !== "" && (
                                  <div className="text-xs text-yellow-700 mt-1">
                                    📝{" "}
                                    {item.specialInstructions}
                                  </div>
                                )}

                            </div>

                            <div className="font-semibold text-slate-800">
                              ₹{lineTotal.toFixed(2)}
                            </div>

                          </div>

                        </div>

                      );
                    }
                  )}

                </div>

              </div>

              {/* FINAL TOTAL */}
              <div className="border-t pt-4 flex justify-between items-center">

                <span className="text-lg font-semibold text-slate-700">
                  Total
                </span>

                <span className="text-2xl font-bold text-green-600">
                  ₹{getTotal(selectedOrder).toFixed(2)}
                </span>

              </div>

            </div>

            {/* FOOTER */}
            <div className="border-t p-4 flex justify-end">

              <button
                onClick={() => setSelectedOrder(null)}
                className="px-5 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-900"
              >
                Close
              </button>

            </div>

          </div>

        </div>

      )}

    </div>
  );
}